import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, LogOut, Download, Search, Eye, BarChart3, Users, DollarSign, Cpu, TrendingUp } from 'lucide-react';
import DhiLogo from './DhiLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_LOGOS = {
  chatgpt: 'https://cdn.prod.website-files.com/686e2126992edd7b040ec218/69ccedef9ce81893c1ef2c17_chatgpt-icon.webp',
  gemini: 'https://cdn.simpleicons.org/googlegemini/0A0F2E',
  perplexity: 'https://cdn.simpleicons.org/perplexity/0A0F2E',
  claude: 'https://cdn.simpleicons.org/anthropic/0A0F2E',
};
const PLATFORM_LABELS = { chatgpt: 'ChatGPT', gemini: 'Gemini', perplexity: 'Perplexity', claude: 'Claude' };

const scoreColor = (s) => (s < 40 ? '#DC2626' : s < 70 ? '#D97706' : '#22c55e');
const gradeColor = (g) => ({ A: '#22c55e', B: '#22c55e', C: '#D97706', D: '#DC2626', F: '#DC2626' }[g] || '#DC2626');

// Status badge — covers the full job lifecycle so admin can see in-flight
// + failed audits, not just the completed ones that used to be all that
// rendered properly.
const STATUS_META = {
  queued:     { label: 'Queued',     bg: '#EEF2FF', fg: '#4338CA' },
  processing: { label: 'Running',    bg: '#FEF3C7', fg: '#92400E' },
  complete:   { label: 'Complete',   bg: '#DCFCE7', fg: '#166534' },
  failed:     { label: 'Failed',     bg: '#FEE2E2', fg: '#991B1B' },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || 'Unknown', bg: '#F3F4F6', fg: '#374151' };
const fmtNum = (n) => { try { return Number(n).toLocaleString(); } catch { return n; } };
const fmtDate = (d) => { try { const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; } catch { return '-'; } };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [audits, setAudits] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState('all');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Auth check
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    fetch(`${API_URL}/api/admin/me`, { headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error(); })
      .catch(() => { localStorage.removeItem('adminToken'); navigate('/admin/login'); });
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, auditsR] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/audits?page=${page}&limit=10&search=${encodeURIComponent(search)}&grade=${grade}&status=${status}`, { headers: authHeaders() }),
      ]);
      if (!statsR.ok || !auditsR.ok) throw new Error();
      const statsD = await statsR.json();
      const auditsD = await auditsR.json();
      setStats(statsD.data);
      setAudits(auditsD.data);
      setPagination(auditsD.pagination);
    } catch {
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [page, search, grade, status, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleExport = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/export?search=${encodeURIComponent(search)}&grade=${grade}`, { headers: authHeaders() });
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `dhi-audits-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleLogout = () => { localStorage.removeItem('adminToken'); localStorage.removeItem('adminEmail'); navigate('/admin/login'); };

  const s = stats || {};

  // Styles
  const headerStyle = { background: '#0A0F2E', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const cardStyle = { background: '#fff', borderRadius: 12, border: '1px solid rgba(10,10,26,0.08)', padding: '18px 20px' };
  const ghostBtn = { padding: '7px 14px', border: '1px solid rgba(10,10,26,0.14)', borderRadius: 8, background: '#fff', color: 'rgba(10,10,26,0.52)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" };

  return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', fontFamily: "'DM Sans', sans-serif" }} data-testid="admin-dashboard">
      {/* Header */}
      <div style={headerStyle} data-testid="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <DhiLogo variant="white" height={22} />
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, color: '#fff' }}>AI Audit Admin</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>AI Visibility Audit Monitor</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/admin/insights')} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }} data-testid="btn-insights"><TrendingUp size={14} /> Insights</button>
          <button onClick={fetchData} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }} data-testid="btn-refresh"><RefreshCw size={14} /> Refresh</button>
          <button onClick={handleLogout} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }} data-testid="btn-logout"><LogOut size={14} /> Logout</button>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>
        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }} data-testid="stats-row">
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={16} color="#2D4DE0" /></div>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)' }}>Total Audits</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0A0A1A', fontFamily: "'Outfit', sans-serif" }}>{s.volume?.total || 0}</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>{s.volume?.today || 0} today</div>
          </div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BarChart3 size={16} color="#DC2626" /></div>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)' }}>Average Score</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0A0A1A', fontFamily: "'Outfit', sans-serif" }}>{s.scores?.average || 0}<span style={{ fontSize: 14, color: 'rgba(10,10,26,0.3)', fontWeight: 400 }}>/100</span></div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>AI visibility score average</div>
          </div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DollarSign size={16} color="#22c55e" /></div>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)' }}>Revenue at Risk</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0A0A1A', fontFamily: "'Outfit', sans-serif" }}>{fmtNum(s.revenue?.total_at_risk || 0)}</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>Across all audited hotels</div>
          </div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#F0F0FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Cpu size={16} color="#2D4DE0" /></div>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)' }}>Platform Average</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0A0A1A', fontFamily: "'Outfit', sans-serif" }}>
              {s.platform_averages ? Math.round((s.platform_averages.chatgpt + s.platform_averages.gemini + s.platform_averages.perplexity + s.platform_averages.claude) / 4) : 0}
              <span style={{ fontSize: 14, color: 'rgba(10,10,26,0.3)', fontWeight: 400 }}>/100</span>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>ChatGPT &middot; Gemini &middot; Perplexity &middot; Claude</div>
          </div>
        </div>

        {/* Grade Distribution */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }} data-testid="grade-distribution">
          <div style={{ ...cardStyle, background: '#FEF2F2' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#DC2626', marginBottom: 6 }}>Critical (D/F)</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#DC2626', fontFamily: "'Outfit', sans-serif" }}>{s.grade_distribution?.critical || 0}</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>Hotels largely invisible to AI</div>
          </div>
          <div style={{ ...cardStyle, background: '#FFFBEB' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#D97706', marginBottom: 6 }}>Needs Work (C)</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#D97706', fontFamily: "'Outfit', sans-serif" }}>{s.grade_distribution?.medium || 0}</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>Partial AI visibility</div>
          </div>
          <div style={{ ...cardStyle, background: '#ECFDF5' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#22c55e', marginBottom: 6 }}>Competitive (A/B)</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#22c55e', fontFamily: "'Outfit', sans-serif" }}>{s.grade_distribution?.good || 0}</div>
            <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>Strong AI presence</div>
          </div>
        </div>

        {/* Platform Averages */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }} data-testid="platform-averages">
          {Object.entries(PLATFORM_LABELS).map(([key, label]) => {
            const avg = s.platform_averages?.[key] || 0;
            return (
              <div key={key} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={PLATFORM_LOGOS[key]} alt={label} width={28} height={28} style={{ borderRadius: 6 }} />
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor(avg), fontFamily: "'Outfit', sans-serif" }}>{avg}<span style={{ fontSize: 11, color: 'rgba(10,10,26,0.3)', fontWeight: 400 }}>/100</span></div>
                </div>
                <div style={{ flex: 1, height: 4, background: '#F5F6FA', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${avg}%`, height: '100%', background: scoreColor(avg), borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Toolbar */}
        <div style={{ ...cardStyle, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }} data-testid="audit-toolbar">
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(10,10,26,0.3)' }} />
            <input
              type="text" placeholder="Search hotel, email, URL, city..."
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              style={{ width: '100%', padding: '10px 12px 10px 34px', border: '1px solid rgba(10,10,26,0.1)', borderRadius: 8, fontSize: 13, color: '#0A0A1A', background: '#F8F9FF', outline: 'none', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" }}
              data-testid="search-input"
            />
          </div>
          <select
            value={grade} onChange={e => { setGrade(e.target.value); setPage(1); }}
            style={{ padding: '10px 14px', border: '1px solid rgba(10,10,26,0.1)', borderRadius: 8, fontSize: 13, color: '#0A0A1A', background: '#F8F9FF', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
            data-testid="grade-filter"
          >
            <option value="all">All Grades</option>
            <option value="A">Grade A</option>
            <option value="B">Grade B</option>
            <option value="C">Grade C</option>
            <option value="D">Grade D</option>
            <option value="F">Grade F</option>
          </select>
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            style={{ padding: '10px 14px', border: '1px solid rgba(10,10,26,0.1)', borderRadius: 8, fontSize: 13, color: '#0A0A1A', background: '#F8F9FF', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
            data-testid="status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="complete">Complete</option>
            <option value="failed">Failed</option>
            <option value="processing">Running</option>
            <option value="queued">Queued</option>
          </select>
          <button onClick={handleExport} style={ghostBtn} data-testid="btn-export"><Download size={14} /> Export CSV</button>
        </div>

        {/* Audits Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }} data-testid="audits-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8F9FF', borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                {['Date', 'Status', 'Hotel Name', 'Email', 'URL', 'Score', 'Grade', 'Duration', 'AI Score', 'Revenue at Risk', ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(10,10,26,0.4)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !audits.length ? (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: 'center', color: 'rgba(10,10,26,0.4)' }}>Loading...</td></tr>
              ) : audits.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: 'center', color: 'rgba(10,10,26,0.4)' }}>No audits found</td></tr>
              ) : audits.map((a, i) => {
                const sm = statusMeta(a.status);
                const isFailed = a.status === 'failed';
                const isPending = a.status === 'queued' || a.status === 'processing';
                return (
                <tr key={a._id || i} style={{ borderBottom: '1px solid rgba(10,10,26,0.05)', cursor: 'pointer', background: isFailed ? '#FEF7F7' : isPending ? '#FFFBEB' : 'transparent' }} onClick={() => navigate(`/admin/audits/${a._id}`)} data-testid={`audit-row-${i}`}>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)' }}>{fmtDate(a.created_at)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: sm.fg, background: sm.bg }}>{sm.label}</span>
                  </td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0A0A1A' }}>{a.hotel_name}</td>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)', fontSize: 12 }}>{a.email}</td>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.4)', fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(a.url || '').slice(0, 30)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    {isPending ? <span style={{ color: 'rgba(10,10,26,0.3)', fontSize: 12 }}>—</span> : isFailed ? <span style={{ color: '#991B1B', fontSize: 12, fontWeight: 600 }}>Failed</span> : (
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: scoreColor(a.final_score || 0) }}>{a.final_score || 0}/100</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    {isPending || isFailed ? <span style={{ color: 'rgba(10,10,26,0.3)', fontSize: 12 }}>—</span> : (
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: gradeColor(a.grade) }}>{a.grade || '-'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      color: (a.duration_s || 0) > 150 ? '#DC2626' : (a.duration_s || 0) > 90 ? '#D97706' : '#22c55e',
                      fontWeight: 600,
                      fontSize: 12,
                      fontFamily: "'DM Sans', sans-serif"
                    }}>
                      {a.duration_s ? `${a.duration_s}s` : '-'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)' }}>
                    {isPending || isFailed ? <span style={{ color: 'rgba(10,10,26,0.3)' }}>—</span> : `${a.ai_visibility_score || 0}/100`}
                  </td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0A0A1A' }}>
                    {isPending || isFailed ? <span style={{ color: 'rgba(10,10,26,0.3)', fontWeight: 400 }}>—</span> : `${a.currency || ''} ${fmtNum(a.room_revenue_at_risk || 0)}`}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <button onClick={e => { e.stopPropagation(); navigate(`/admin/audits/${a._id}`); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2D4DE0' }} data-testid={`btn-view-${i}`}><Eye size={16} /></button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderTop: '1px solid rgba(10,10,26,0.08)' }} data-testid="pagination">
              <span style={{ fontSize: 12, color: 'rgba(10,10,26,0.4)' }}>
                Showing {((page - 1) * 10) + 1}–{Math.min(page * 10, pagination.total)} of {pagination.total}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ ...ghostBtn, opacity: page <= 1 ? 0.4 : 1 }}>Previous</button>
                <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} style={{ ...ghostBtn, opacity: page >= pagination.totalPages ? 0.4 : 1 }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
